import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconFileText,
  IconHelp,
  IconLoader2,
  IconMessageCircle,
  IconMicrophone,
  IconSearch,
  IconSpeakerphone,
  IconX,
} from '@tabler/icons-react';
import { searchAPI } from '../api/client';
import { SHADOW } from '../utils/uiStyles';

// 색은 --gw-* 토큰(라이트 기본값 = 폴백, public/index.html 정의) — 시안 F `.modal` 계열(g1-039).
const T = {
  text: 'var(--gw-text, #32312e)',
  sub: 'var(--gw-secondary, #64635f)',
  muted: 'var(--gw-muted, #92918d)',
  border: 'var(--gw-border, #e5e5e2)',
  surface: 'var(--gw-surface, #fff)',
  bg: 'var(--gw-bg, #fff)',
  hover: 'var(--gw-hover, #efefed)',
  overlay: 'var(--gw-overlay, #19191945)',
  danger: 'var(--gw-red-ink, #a4564d)',
};

const GROUPS = [
  { key: 'conversations', label: '대화', Icon: IconMessageCircle },
  { key: 'documents', label: '문서', Icon: IconFileText },
  { key: 'meetings', label: '회의록', Icon: IconMicrophone },
  { key: 'board', label: '게시판', Icon: IconSpeakerphone },
  { key: 'inquiries', label: '문의', Icon: IconHelp },
];

function fmtWhen(value) {
  if (!value) return '';
  try {
    const d = new Date(String(value).endsWith('Z') ? value : `${value}Z`);
    return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function GlobalSearch({ open, onOpen, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const titleId = useId();
  const [query, setQuery] = useState('');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const grouped = useMemo(() => GROUPS.map((g) => ({
    ...g,
    items: (data[g.key] || []).map((item) => ({ ...item, group: g })),
  })).filter((g) => g.items.length > 0), [data]);

  const flatItems = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  const close = useCallback(() => {
    onClose?.();
    setQuery('');
    setData({});
    setError('');
    setActiveIndex(-1);
  }, [onClose]);

  // 열릴 때 초점이 있던 요소(서랍 안 «검색» 단추 등)를 기억해 둔다 — 닫을 때 돌려주려고.
  const returnFocusRef = useRef(null);
  useEffect(() => {
    if (open) returnFocusRef.current = document.activeElement;
  }, [open]);

  // 닫기(Escape · × · 덮개) = close + 초점 복귀. 항목으로 이동(go)은 화면이 바뀌니 복귀 없이 close 만 쓴다.
  const dismiss = useCallback(() => {
    const el = returnFocusRef.current;
    returnFocusRef.current = null;
    close();
    if (el && el !== document.body && el.isConnected && !el.disabled && typeof el.focus === 'function') el.focus();
  }, [close]);

  const go = useCallback((item) => {
    if (!item?.url) return;
    navigate(item.url);
    close();
  }, [close, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      const key = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === 'k') {
        e.preventDefault();
        onOpen?.();
        return;
      }
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => flatItems.length ? Math.min(i + 1, flatItems.length - 1) : -1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => flatItems.length ? Math.max(i - 1, 0) : -1);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        go(flatItems[activeIndex]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, dismiss, flatItems, go, onOpen, open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!q) {
      setData({});
      setError('');
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      searchAPI.search(q)
        .then((r) => {
          if (!cancelled) setData(r.data || {});
        })
        .catch(() => {
          if (!cancelled) {
            setData({});
            setError('검색 결과를 불러오지 못했습니다.');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query]);

  useEffect(() => {
    setActiveIndex(flatItems.length ? 0 : -1);
  }, [flatItems.length]);

  if (!open) return null;

  return (
    <div onMouseDown={(e) => { e.preventDefault(); dismiss(); }} style={backdrop}>
      {/* 시안 F `.modal` — 제목 h2(20px/700) + × + 입력 + 결과 목록. 대화상자 이름은 그대로 «통합 검색»(h2 가 이름이 된다). */}
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} data-gw-escape-layer="" onMouseDown={(e) => e.stopPropagation()} style={panel}>
        <div style={headRow}>
          <h2 id={titleId} style={title}>통합 검색</h2>
          <button type="button" className="gw-gs-close" onClick={dismiss} aria-label="닫기" title="닫기" style={closeBtn}><IconX size={18} stroke={1.9} /></button>
        </div>
        <div style={searchRow}>
          <IconSearch size={18} stroke={1.9} style={{ color: T.sub, flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="대화, 문서, 회의록, 게시판, 문의 검색"
            style={input}
          />
          {loading && <IconLoader2 size={18} stroke={2} style={{ color: T.muted, animation: 'gw-global-search-spin .8s linear infinite', flexShrink: 0 }} />}
        </div>
        {/* × 단추의 바탕·글자색은 인라인이 아니라 여기 둔다 — 인라인이면 :hover 규칙이 이기지 못한다(인라인 > 스타일시트). */}
        <style>{`
          @keyframes gw-global-search-spin{to{transform:rotate(360deg)}}
          .gw-gs-close{background:transparent;color:var(--gw-muted,#92918d)}
          .gw-gs-close:hover:not(:disabled){background:var(--gw-hover,#efefed);color:var(--gw-text,#32312e)}
        `}</style>
        <div style={body}>
          {!query.trim() && <div style={empty}>검색어를 입력하십시오.</div>}
          {query.trim() && error && <div style={{ ...empty, color: T.danger }}>{error}</div>}
          {query.trim() && !error && !loading && flatItems.length === 0 && <div style={empty}>검색 결과가 없습니다.</div>}
          {grouped.map((group) => (
            <section key={group.key} style={{ marginBottom: '12px' }}>
              <div style={groupTitle}>{group.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {group.items.map((item) => {
                  const idx = flatItems.indexOf(item);
                  const active = idx === activeIndex;
                  const Icon = group.Icon;
                  return (
                    <button
                      key={`${group.key}-${item.id}-${idx}`}
                      type="button"
                      onClick={() => go(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      disabled={!item.url}
                      style={resultBtn(active, !!item.url)}
                    >
                      <Icon size={18} stroke={1.8} style={{ color: active ? T.text : T.sub, flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={titleLine}>{item.title || '제목 없음'}</span>
                        <span style={snippetLine}>{item.snippet || (group.key === 'documents' ? 'AI 채팅에서 학습자료 답변으로 확인하십시오.' : '')}</span>
                      </span>
                      <span style={whenLine}>{fmtWhen(item.when)}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

const backdrop = {
  position: 'fixed',
  inset: 0,
  zIndex: 3000,
  background: T.overlay,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '10vh 14px 24px',
  boxSizing: 'border-box',
};

// 시안 F `.modal` — 폭 520(max 100%) · 반경 10 · 패딩 25 · 테두리 --border · 그림자 --float-shadow · 배경 surface
const panel = {
  width: '520px',
  maxWidth: '100%',
  maxHeight: 'min(720px, 82dvh)',
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: '10px',
  boxShadow: SHADOW.float,
  padding: '25px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

// 시안 F `.modal h2`
const title = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 700,
  color: T.text,
};

const searchRow = {
  marginTop: '20px',
  minHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0 10px',
  border: `1px solid ${T.border}`,
  borderRadius: '5px',
  background: T.bg,
  boxSizing: 'border-box',
  flexShrink: 0,
};

const input = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  fontSize: '16px',
  color: T.text,
  background: 'transparent',
};

const closeBtn = {
  width: '28px',
  height: '28px',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const body = {
  marginTop: '12px',
  overflowY: 'auto',
  minHeight: '170px',
};

const empty = {
  padding: '36px 12px',
  textAlign: 'center',
  color: T.sub,
  fontSize: '14px',
  fontWeight: 600,
};

const groupTitle = {
  padding: '8px 8px 6px',
  fontSize: '12px',
  color: T.sub,
  fontWeight: 800,
};

// 시안 F `.modal-search-results button` — 패딩 8 · 반경 5 · hover(= 키보드·마우스로 고른 행) 바탕 --hover
const resultBtn = (active, clickable) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px',
  border: 'none',
  borderRadius: '5px',
  background: active ? T.hover : 'transparent',
  color: T.text,
  textAlign: 'left',
  cursor: clickable ? 'pointer' : 'default',
  opacity: clickable ? 1 : 0.82,
  boxSizing: 'border-box',
});

const titleLine = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 700,
  color: T.text,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const snippetLine = {
  display: 'block',
  marginTop: '3px',
  fontSize: '12px',
  lineHeight: 1.35,
  color: T.sub,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const whenLine = {
  marginLeft: '8px',
  fontSize: '11px',
  color: T.muted,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
